import PropTypes from 'prop-types';

import { proseMirrorDocShape } from '../../../../_shared';

import {
	CONTINUATION_TYPES,
	FIELD_TYPES,
	FIELD_SUB_TYPES,
	FORM_TYPES,
} from '../../../constants';

/**
 * Expected object shape for CRUD actions related to the form editor.
 */
export const actionsShape = PropTypes.shape({

	/**
	 * Callback function to handle form submission.
	 */
	submitForm: PropTypes.func.isRequired,

	/**
	 * Callback function to update field-level data for the form.
	 */
	updateForm: PropTypes.func.isRequired,

	/**
	 * Callback function to update the language for the form.
	 */
	updateLanguage: PropTypes.func.isRequired,

	/**
	 * Callback function to add a new translation for the form.
	 */
	addTranslation: PropTypes.func.isRequired,

	/**
	 * Callback function to remove a translation from the form.
	 */
	deleteTranslation: PropTypes.func.isRequired,

	/**
	 * Callback function to convert the form data to the final form to preview.
	 */
	generateFormPreview: PropTypes.func.isRequired,

	/**
	 * Actions related to form sections.
	 */
	sections: PropTypes.shape({

		/**
		 * Callback function to add a new form section.
		 */
		add: PropTypes.func.isRequired,

		/**
		 * Callback function to delete a form section.
		 */
		delete: PropTypes.func.isRequired,

		/**
		 * Callback function to move a form section.
		 */
		move: PropTypes.func.isRequired,

		/**
		 * Callback function to update field-level data for a form section.
		 */
		update: PropTypes.func.isRequired,

		/**
		 * Callback function to update the advanced settings for a form section.
		 */
		updateAdvancedSetting: PropTypes.func.isRequired,

	}).isRequired,

	/**
	 * Actions related to form fields.
	 */
	fields: PropTypes.shape({

		/**
		 * Callback function to add a new form field.
		 */
		add: PropTypes.func.isRequired,

		/**
		 * Callback function to clone an existing form field.
		 */
		clone: PropTypes.func.isRequired,

		/**
		 * Callback function to delete a form field.
		 */
		delete: PropTypes.func.isRequired,

		/**
		 * Callback function to move a form field.
		 */
		move: PropTypes.func.isRequired,

		/**
		 * Callback function to update field-level data for a form field.
		 */
		update: PropTypes.func.isRequired,

	}).isRequired,

	/**
	 * Actions related to form field options.
	 */
	options: PropTypes.shape({

		/**
		 * Callback function to add a new form field option.
		 */
		add: PropTypes.func.isRequired,

		/**
		 * Callback function to delete a form field option.
		 */
		delete: PropTypes.func.isRequired,

		/**
		 * Callback function to move a form field option.
		 */
		move: PropTypes.func.isRequired,

		/**
		 * Callback function to update field-level data for a form field option.
		 */
		update: PropTypes.func.isRequired,

	}).isRequired,

});

/**
 * Expected object shape for form data in the form editor.
 */
export const formDataShape = PropTypes.shape({

	/**
	 * The unique ID of the form.
	 */
	id: PropTypes.string,

	/**
	 * The form type.
	 */
	type: PropTypes.oneOf(Object.values(FORM_TYPES)).isRequired,

});

/**
 * Expected object shape for form field option data in the form editor.
 */
export const formFieldOptionDataShape = PropTypes.shape({

	/**
	 * The option ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The option value.
	 */
	value: PropTypes.string,

	/**
	 * True, if the option is a correct answer choice.
	 */
	is_correct_answer: PropTypes.bool,

	/**
	 * The type of conditional navgiation setting for the form field option.
	 */
	continuation_type: PropTypes.oneOf([
		CONTINUATION_TYPES.SECTION,
		CONTINUATION_TYPES.DEFAULT,
		CONTINUATION_TYPES.SUBMIT,
	]),

	/**
	 * The ID of the proceeding form section if this option is chosen.
	 */
	continuation_section_id: PropTypes.string,

	/**
	 * The weight of the option, used for ordering the options.
	 */
	weight: PropTypes.number.isRequired,

	/**
	 * The multi-language content for the form field option.
	 */
	form_field_option_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The option label.
		 */
		label: PropTypes.string,

	})).isRequired,

});

/**
 * Expected object shape for form field data in the form editor.
 */
export const formFieldDataShape = PropTypes.shape({

	/**
	 * The field ID.
	 */
	id: PropTypes.string,

	/**
	 * The field type.
	 */
	type: PropTypes.oneOf([
		FIELD_TYPES.CHECKBOX,
		FIELD_TYPES.CHECKBOXES,
		FIELD_TYPES.EMAIL,
		FIELD_TYPES.FILE_UPLOAD,
		FIELD_TYPES.LIKERT,
		FIELD_TYPES.LOCATION,
		FIELD_TYPES.PERCENTAGE,
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
		FIELD_TYPES.SOCIAL_MEDIA,
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
	]),

	/**
	 * The field subtype (if any, only for SOCIAL_MEDIA fields).
	 */
	subtype: PropTypes.oneOf([
		FIELD_SUB_TYPES.EMAIL,
		FIELD_SUB_TYPES.FACEBOOK,
		FIELD_SUB_TYPES.LINKEDIN,
		FIELD_SUB_TYPES.TWITTER,
	]),

	/**
	 * The field privacy level (if any).
	 */
	privacy: PropTypes.number,

	/**
	 * Flag indicating if the field is required.
	 */
	required: PropTypes.bool,

	/**
	 * Flag indicating if the field can be used as a user filter
	 * (only for certain profile form field types).
	 */
	filter: PropTypes.bool,

	/**
	 * Flag indicating if the associated form field options
	 * have navigational conditions.
	 */
	conditional: PropTypes.bool,

	/**
	 * The list of options for the field.
	 */
	form_field_options: PropTypes.arrayOf(formFieldOptionDataShape),

	/**
	 * The multi-language content for the form field.
	 */
	form_field_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The title, i.e. label text.
		 */
		title: PropTypes.string,

		/**
		 * The helper text.
		 */
		help: proseMirrorDocShape,

		/**
		 * The placeholder text.
		 */
		placeholder: PropTypes.string,

		/**
		 * The correct submission feedback text.
		 */
		submission_feedback_correct: proseMirrorDocShape,

		/**
		 * The incorrect submission feedback text.
		 */
		submission_feedback_incorrect: proseMirrorDocShape,

	})).isRequired,

});

/**
 * Expected object shape for form section data in the form editor.
 */
export const formSectionDataShape = PropTypes.shape({

	/**
	 * The unique ID of the section.
	 */
	id: PropTypes.string,

	/**
	 * The type of conditional navigation setting for the form section.
	 */
	continuation_type: PropTypes.oneOf([
		CONTINUATION_TYPES.SECTION,
		CONTINUATION_TYPES.DEFAULT,
		CONTINUATION_TYPES.SUBMIT,
		CONTINUATION_TYPES.PRODUCT_TICKETS,
		CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS,
	]),

	/**
	 * The ID of the proceeding form section for section continuation types.
	 */
	continuation_section_id: PropTypes.string,

	/**
	 * The list of form fields within this section.
	 */
	fields: PropTypes.arrayOf(formFieldDataShape).isRequired,

	/**
	 * The list of advanced settings for the form section.
	 */
	advanced_settings: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The form section advanced setting ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The type of form setting which corresponds to the form section's continuation type.
		 */
		object_type: PropTypes.oneOf([
			CONTINUATION_TYPES.PRODUCT_TICKETS,
			CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS,
		]),

		/**
		 * The form section advanced setting object ID, either a product ticket or a
		 * product ticket group ID.
		 */
		object_id: PropTypes.string.isRequired,

		/**
		 * The type of conditional navigation selected.
		 */
		continuation_type: PropTypes.oneOf([
			CONTINUATION_TYPES.DEFAULT,
			CONTINUATION_TYPES.SECTION,
			CONTINUATION_TYPES.SUBMIT,
		]),

		/**
		 * The ID of the proceeding form section for section continuation
		 * types.
		 */
		continuation_section_id: PropTypes.string,

	})).isRequired,

	/**
	 * The multi-language content for the form section.
	 */
	form_section_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The title.
		 */
		title: PropTypes.string,

		/**
		 * The description.
		 */
		description: PropTypes.string,

	})).isRequired,

});

export const fieldOptionInputValidationShape = PropTypes.shape({

	/**
	 * Validation related to the value input.
	 */
	value: PropTypes.shape({

		/**
		 * True, if the input is invalid.
		 */
		invalid: PropTypes.bool.isRequired,

		/**
		 * The error message, if invalid.
		 */
		error: PropTypes.string,

	}),

});

/**
 * Expected object shape for input validation related to the
 * options for a field in the form editor.
 */
export const fieldOptionsInputValidationShape = PropTypes.shape({

	/**
	 * True, if the list of options is invalid.
	 */
	invalid: PropTypes.bool.isRequired,

	/**
	 * The error message, if the list of options is invalid.
	 */
	error: PropTypes.string,

	/**
	 * Data keyed by option ID.
	 */
	data: PropTypes.objectOf(fieldOptionInputValidationShape).isRequired,

});

/**
 * Expected object shape for input validation related to a field in the form editor.
 */
export const fieldInputValidationShape = PropTypes.shape({

	/**
	 * Validation related to the title input.
	 */
	title: PropTypes.shape({

		/**
		 * True, if the input is invalid.
		 */
		invalid: PropTypes.bool.isRequired,

		/**
		 * The error message, if invalid.
		 */
		error: PropTypes.string,

	}),

	/**
	 * Validation related to the form field options.
	 */
	form_field_options: fieldOptionsInputValidationShape,

});

/**
 * Expected object shape for input validation related to a section in the form editor.
 */
export const sectionInputValidationShape = PropTypes.shape({

	/**
	 * Validation related to the form fields.
	 */
	fields: PropTypes.shape({

		/**
		 * Data keyed by field ID.
		 */
		data: PropTypes.objectOf(fieldInputValidationShape).isRequired,

	}).isRequired,

	/**
	 * Validation related to the multi-language content.
	 */
	form_section_contents: PropTypes.shape({

		/**
		 * Data keyed by language, i.e. "en".
		 */
		data: PropTypes.objectOf(PropTypes.shape({

			/**
			 * Validation related to the title input.
			 */
			title: PropTypes.shape({

				/**
				 * True, if the input is invalid.
				 */
				invalid: PropTypes.bool.isRequired,

				/**
				 * The error message, if invalid.
				 */
				error: PropTypes.string,

			}),

		})),

	}).isRequired,

});

/**
 * Expected object shape for input validation related to the form editor.
 */
export const inputValidationShape = PropTypes.shape({

	/**
	 * Validation related to the form sections.
	 */
	sections: PropTypes.shape({

		/**
		 * Data keyed by section ID.
		 */
		data: PropTypes.objectOf(sectionInputValidationShape).isRequired,

	}).isRequired,

});
